/**
 * Uploads that survive a short network drop — the mobile case.
 *
 * An upload used to be ONE request with no second chance: a few seconds
 * without signal, a lift going down, the phone switching from wifi to 4G, and
 * the whole file was lost with an error (BOLLOPRINT-TENANT-BACKEND-5Q: a new
 * workshop lost two STL uploads from an Android phone at once and left).
 *
 * Here an attempt that fails for a NETWORK reason is tried again, from the
 * start, a few times: after a pause when the browser says it is online, or as
 * soon as it comes back online when it says it is not. A refusal (a 4xx: too
 * big, over quota, wrong type) is never retried — repeating it gives the same
 * answer. Each attempt starts from zero: a single PUT cannot be resumed, and a
 * failed one leaves nothing in the bucket (S3 writes an object whole or not
 * at all).
 *
 * What this does NOT survive: the page itself going away (tab closed, the
 * browser killed in the background). The browser forgets the file then, and
 * nobody can continue the upload without the user picking it again. While an
 * upload runs, `hooks/useUploads.ts` keeps the screen on (a locked phone
 * freezes the page) — the visible part is the progress ring on the file.
 *
 * Shared by the whole web (both SPAs). Pure: the network, the clock and the
 * online signal are injected, so the rules are tested in Node
 * (`tenant/frontend/services/resilientUpload.test.ts`).
 */
import type { PostForm } from './xhr.ts';

/** What the user should see while the upload runs. */
export type UploadState =
    | { readonly kind: 'uploading'; readonly loaded: number; readonly total: number }
    /** The last attempt failed for a network reason; the next one is coming.
     *  `offline`: the browser says there is no connection — waiting for it. */
    | { readonly kind: 'retrying'; readonly attempt: number; readonly offline: boolean };

/** What a file row or thumbnail shows: the upload running, waiting to retry,
 *  or given up (`message` is for a tooltip; the row says «couldn't upload»). */
/** `retryable: false` = the server REFUSED the file (wrong type, too big,
 *  no space…): sending the same file again gets the same answer, so the row
 *  says why and offers no «Reintentar». On 2026-10-27 a workshop pressed it
 *  seven times on a file type we never take, with only «No se pudo subir» on
 *  the row once the toast was gone. */
export type UploadProgress = UploadState | { readonly kind: 'failed'; readonly message: string; readonly retryable?: boolean };

export interface RetryEnv {
    /** `navigator.onLine` — a `false` is reliable, a `true` is only a hint. */
    online(): boolean;
    /** Resolves when the browser is back online (or after `timeoutMs`). */
    waitOnline(timeoutMs: number, signal?: AbortSignal): Promise<void>;
    sleep(ms: number, signal?: AbortSignal): Promise<void>;
}

/** Pauses between attempts (ms): attempt 2 waits the first, and so on. */
export const RETRY_PAUSES_MS = [2_000, 5_000, 10_000] as const;
/** At most this long waiting for the connection to come back, per attempt. */
export const OFFLINE_WAIT_MS = 60_000;

/** Thrown by an attempt that failed for a network reason (worth retrying). */
export class NetworkUploadError extends Error {
    constructor(message = 'network') {
        super(message);
        this.name = 'NetworkUploadError';
    }
}

export function isNetworkUploadError(err: unknown): err is NetworkUploadError {
    return err instanceof Error && err.name === 'NetworkUploadError';
}

/** An HTTP status that is worth a second try: no answer at all (0), the
 *  server overloaded or down (5xx), or asked to slow down (429). */
export function isRetriableStatus(status: number): boolean {
    return status === 0 || status === 429 || status >= 500;
}

/** True when `error` is the server saying no to THIS file (a 4xx with a
 *  status: `UploadRefusedError`, the tenant's `ApiError`…), not a cut
 *  connection. 401 (a session that may come back), 408 and 429 are not. */
export function isUploadRefusal(error: unknown): boolean {
    const status = (error as { status?: unknown } | null)?.status;
    return typeof status === 'number' && status >= 400 && status < 500
        && status !== 401 && status !== 408 && !isRetriableStatus(status);
}

/**
 * Run `attempt` until it succeeds, it fails for a reason that is not the
 * network (`isRetriable` false — rethrown at once), or the attempts run out
 * (the last error is rethrown). `onRetry` is told before each wait.
 */
export async function withUploadRetry<T>(
    attempt: (n: number) => Promise<T>,
    env: RetryEnv,
    options: {
        isRetriable: (err: unknown) => boolean;
        onRetry?: (state: Extract<UploadState, { kind: 'retrying' }>) => void;
        signal?: AbortSignal;
    },
): Promise<T> {
    const maxAttempts = RETRY_PAUSES_MS.length + 1;
    for (let n = 1; ; n += 1) {
        try {
            return await attempt(n);
        } catch (err) {
            if (options.signal?.aborted) throw err;
            if (n >= maxAttempts || !options.isRetriable(err)) throw err;
            const offline = !env.online();
            options.onRetry?.({ kind: 'retrying', attempt: n + 1, offline });
            if (offline) await env.waitOnline(OFFLINE_WAIT_MS, options.signal);
            else await env.sleep(RETRY_PAUSES_MS[n - 1] as number, options.signal);
        }
    }
}

// ── A 3D file straight to the bucket ────────────────────────────────────────

export interface DirectUploadDeps {
    /** Pre-flight: rejects when the file changed on disk after it was picked
     *  (not retried: the user has to pick it again). */
    readable(file: File): Promise<void>;
    /** `POST /upload/presign` → a single-use PUT URL and its bucket key. */
    presign(file: File): Promise<{ upload_url: string; key: string }>;
    /** The PUT, with progress; resolves with the HTTP status (0 = no answer). */
    put(request: { url: string; body: Blob; contentType: string; onProgress: (loaded: number) => void; signal?: AbortSignal }): Promise<number>;
    /** `POST /upload/complete` → checks the object and counts it in the quota. */
    complete(key: string, size: number): Promise<{ url: string; size: number }>;
    /** True for a failure of `presign` / `complete` that is the network or a
     *  5xx (worth retrying), false for a refusal. */
    isRetriable(err: unknown): boolean;
}

export interface DirectUploadResult {
    /** The bucket key, `tenants/{slug}/designs/temp/{name}`. */
    key: string;
    /** The same object relative to the workshop's folder (`designs/temp/{name}`):
     *  what quick-create takes as `stl_temp_key`. */
    tempKey: string;
    /** The storage URL `/upload/complete` answered (`/storage/{slug}/…`). */
    url: string;
    size: number;
}

/** `tenants/{slug}/designs/temp/x` → `designs/temp/x`. */
export function workshopRelativeKey(key: string): string {
    const parts = key.split('/');
    if (parts.length < 3 || parts[0] !== 'tenants') throw new Error(`unexpected bucket key: ${key}`);
    return parts.slice(2).join('/');
}

/**
 * Presign → PUT → complete, the whole sequence retried on a network failure.
 * A retry asks for a NEW URL (the old one may be the thing that failed, and a
 * presign is cheap); the object a failed PUT never wrote costs nothing.
 */
export async function uploadDirectWithRetry(
    file: File,
    deps: DirectUploadDeps,
    env: RetryEnv,
    options: {
        onState?: (state: UploadState) => void;
        signal?: AbortSignal;
        /** The bucket route's cap, checked before asking for a URL (see
         *  `uploadMultipartWithRetry`). */
        maxBytes?: number;
    } = {},
): Promise<DirectUploadResult> {
    if (options.maxBytes !== undefined && file.size > options.maxBytes) {
        throw fileTooLarge(options.maxBytes);
    }
    const contentType = file.type || 'application/octet-stream';
    return withUploadRetry(
        async () => {
            await deps.readable(file);
            const presigned = await deps.presign(file);
            options.onState?.({ kind: 'uploading', loaded: 0, total: file.size });
            const status = await deps.put({
                url: presigned.upload_url,
                body: file,
                contentType,
                onProgress: (loaded) => options.onState?.({ kind: 'uploading', loaded, total: file.size }),
                signal: options.signal,
            });
            if (status < 200 || status >= 300) {
                if (isRetriableStatus(status)) throw new NetworkUploadError(`put status ${status}`);
                throw new Error(`Direct upload refused: ${status}`);
            }
            const done = await deps.complete(presigned.key, file.size);
            return { key: presigned.key, tempKey: workshopRelativeKey(presigned.key), url: done.url, size: done.size };
        },
        env,
        {
            isRetriable: (err) => isNetworkUploadError(err) || deps.isRetriable(err),
            onRetry: options.onState,
            signal: options.signal,
        },
    );
}

// ── A file to our own backend (multipart POST) ──────────────────────────────

/** The backend refused the upload (a 4xx: too big, over quota, wrong type).
 *  `detail` is the response's `detail` when it had one — a code object or a
 *  text; never shown raw to a workshop (the caller translates it). */
export class UploadRefusedError extends Error {
    readonly status: number;
    readonly detail: unknown;
    constructor(status: number, detail: unknown) {
        super(typeof detail === 'string' && detail ? detail : `upload refused: ${status}`);
        this.name = 'UploadRefusedError';
        this.status = status;
        this.detail = detail;
    }
}

const MB = 1024 * 1024;

/** The refusal of a file over `maxBytes`: the same body the server's door
 *  answers (`api_error("file_too_large_max", params={"max_mb"})`), so each
 *  app translates ONE thing — the tenant `errors.file_too_large_max` in the
 *  user's language, the admin its Spanish `message`. */
export function fileTooLarge(maxBytes: number): UploadRefusedError {
    const maxMb = Math.max(1, Math.floor(maxBytes / MB));
    return new UploadRefusedError(413, {
        code: 'file_too_large_max',
        params: { max_mb: maxMb },
        message: `El archivo pesa más de ${maxMb} MB, que es el máximo aquí.`,
    });
}

export interface MultipartTarget {
    readonly url: string;
    /** The form field the file goes in (default `file`). */
    readonly field?: string;
    /** Other form fields sent with the file. */
    readonly fields?: Readonly<Record<string, string>>;
    readonly headers?: Readonly<Record<string, string>>;
    readonly withCredentials?: boolean;
}

export interface MultipartDeps {
    /** Pre-flight: rejects when the file changed on disk (never retried). */
    readable(file: File): Promise<void>;
    post: PostForm;
}

function parseBody(text: string): unknown {
    if (!text) return {};
    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

/**
 * POST the file as `multipart/form-data`, with real progress and retried on a
 * network failure (each attempt from zero: a multipart body cannot resume).
 * Resolves with the parsed JSON answer; a refusal rejects with
 * `UploadRefusedError`.
 */
export async function uploadMultipartWithRetry(
    file: File,
    target: MultipartTarget,
    deps: MultipartDeps,
    env: RetryEnv,
    options: {
        onState?: (state: UploadState) => void;
        signal?: AbortSignal;
        /** Never repeat the POST: for a request that does something besides
         *  storing the file (a support reply posted to a Discord thread), where
         *  a lost answer to a request that DID arrive would do it twice. */
        noRetry?: boolean;
        /** The route's cap, checked BEFORE sending: a file over it is refused
         *  at once (`UploadRefusedError` 413 `file_too_large`, the same answer
         *  the server's door gives) instead of uploading for minutes to be
         *  turned away. The server checks again: a browser can be bypassed. */
        maxBytes?: number;
    } = {},
): Promise<unknown> {
    if (options.maxBytes !== undefined && file.size > options.maxBytes) {
        throw fileTooLarge(options.maxBytes);
    }
    return withUploadRetry(
        async () => {
            await deps.readable(file);
            const form = new FormData();
            form.append(target.field ?? 'file', file);
            for (const [name, value] of Object.entries(target.fields ?? {})) form.append(name, value);
            options.onState?.({ kind: 'uploading', loaded: 0, total: file.size });
            const answer = await deps.post({
                url: target.url,
                body: form,
                headers: target.headers,
                withCredentials: target.withCredentials,
                signal: options.signal,
                // The body is the file plus a few hundred bytes of multipart
                // framing: shown as a share of the FILE, which is what the
                // user picked.
                onProgress: (loaded, total) => options.onState?.({
                    kind: 'uploading',
                    loaded: total > 0 ? Math.min(file.size, Math.round((loaded / total) * file.size)) : 0,
                    total: file.size,
                }),
            });
            if (answer.status >= 200 && answer.status < 300) return parseBody(answer.text);
            if (isRetriableStatus(answer.status)) throw new NetworkUploadError(`post status ${answer.status}`);
            const body = parseBody(answer.text);
            const detail = typeof body === 'object' && body !== null && 'detail' in body
                ? (body as { detail: unknown }).detail
                : body;
            throw new UploadRefusedError(answer.status, detail);
        },
        env,
        {
            isRetriable: (err) => !options.noRetry && isNetworkUploadError(err),
            onRetry: options.onState,
            signal: options.signal,
        },
    );
}

// ── The browser's environment ───────────────────────────────────────────────

function abortError(): Error {
    const error = new Error('aborted');
    error.name = 'AbortError';
    return error;
}

export function browserRetryEnv(): RetryEnv {
    return {
        online: () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false),
        sleep: (ms, signal) =>
            new Promise<void>((resolve, reject) => {
                if (signal?.aborted) return reject(abortError());
                const timer = setTimeout(resolve, ms);
                signal?.addEventListener('abort', () => {
                    clearTimeout(timer);
                    reject(abortError());
                }, { once: true });
            }),
        waitOnline: (timeoutMs, signal) =>
            new Promise<void>((resolve, reject) => {
                if (signal?.aborted) return reject(abortError());
                const done = () => {
                    clearTimeout(timer);
                    window.removeEventListener('online', done);
                    resolve();
                };
                const timer = setTimeout(done, timeoutMs);
                window.addEventListener('online', done);
                signal?.addEventListener('abort', () => {
                    clearTimeout(timer);
                    window.removeEventListener('online', done);
                    reject(abortError());
                }, { once: true });
            }),
    };
}
